import React, { useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import MlxButton from '../../components/common/MlxButton/MlxButton';
import '../reset-password/resetPassword.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePublicBrandingLogo } from '../../hooks/usePublicBrandingLogo';
import SoltBrandingLogo from '../../components/common/SoltBrandingLogo/SoltBrandingLogo';
import { routes } from '../../routes/allRoutes';
import { AlertVariant, APP_NAME, APP_YEAR } from '../../constants/constants';
import { Button } from 'primereact/button';
import { Messages } from 'primereact/messages';
import { useMutation } from 'react-query';
import { resetNewPassword } from '../../helpers/authHelper';
import {
    CONFIRM_PASSWORD,
    SET_NEW_PASSWORD_MESSAGE,
    FOOTER_BRAND,
    FORGOT_PASSWORD,
    LOGIN,
    NEW_PASSWORD,
    PASSWORD,
    PASSWORD_CHECK_LOWERCASE,
    PASSWORD_CHECK_MIN,
    PASSWORD_CHECK_NUMBER,
    PASSWORD_CHECK_SPECIAL,
    PASSWORD_CHECK_UPPERCASE,
    REMEMBER_ME,
    SET_NEW_PASSWORD,
    USERNAME,
} from '../../constants/strings';
import { useTranslation } from '../../hooks/useTranslation';
import { useFormik } from 'formik';
import { checkPasswordCriteria, initialResetPasswordFormData, ResetPasswordSchema } from '../../helpers/registerAccountHelper';
import HttpServiceHelper from '../../helpers/httpServiceHelper';
import { _ToastMessageProps } from '../../constants/staticTypes';
import { defaultToastData } from '../../helpers/commonHelper';
import MlxToast from '../../components/common/MlxToast/MlxToast';

const languageTexts = {
    REMEMBER_ME: REMEMBER_ME,
    USERNAME: USERNAME,
    PASSWORD: PASSWORD,
    FORGOT_PASSWORD: FORGOT_PASSWORD,
    FOOTER_BRAND: FOOTER_BRAND,
    LOGIN: LOGIN,
    NEW_PASSWORD: NEW_PASSWORD,
    CONFIRM_PASSWORD: CONFIRM_PASSWORD,
    SET_NEW_PASSWORD: SET_NEW_PASSWORD,
    SET_NEW_PASSWORD_MESSAGE: SET_NEW_PASSWORD_MESSAGE,
    PASSWORD_CHECK_LOWERCASE: PASSWORD_CHECK_LOWERCASE,
    PASSWORD_CHECK_UPPERCASE: PASSWORD_CHECK_UPPERCASE,
    PASSWORD_CHECK_NUMBER: PASSWORD_CHECK_NUMBER,
    PASSWORD_CHECK_MIN: PASSWORD_CHECK_MIN,
    PASSWORD_CHECK_SPECIAL: PASSWORD_CHECK_SPECIAL,
};

const SetNewPassword = () => {
    const { logo, isLoading: isLogoLoading } = usePublicBrandingLogo();
    const navigate = useNavigate();
    const messageRef = useRef<Messages>(null);
    const translatedText = useTranslation(languageTexts, 'capitalize');
    const resetPasswordMutation = useMutation(HttpServiceHelper);
    const [searchParams] = useSearchParams();
    const code = searchParams.get('code');
    const userName = searchParams.get('user');

    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);

    // Formik setup
    const resetPasswordForm = useFormik({
        enableReinitialize: true,
        initialValues: { ...initialResetPasswordFormData },
        validationSchema: ResetPasswordSchema,
        onSubmit: (values) => {
            setIsLoading(true);
            resetNewPassword(
                resetPasswordMutation,
                { userName: userName ?? '', code: code ?? '', password: values.password, isAcceptedPrivacyPolicy: true },
                (data) => {
                    setToastMessage({
                        message: data?.message ?? '',
                        variant: AlertVariant.SUCCESS,
                        show: true,
                    });
                    setIsLoading(false);
                    navigate(routes.login);
                },
                (error: string, variant: string) => {
                    setToastMessage({
                        message: error,
                        variant,
                        show: true,
                    });
                    setIsLoading(false);
                }
            );
        },
    });

    const passwordCriteria = checkPasswordCriteria(resetPasswordForm.values.password);
    const PasswordRequirement = ({ className, text, isValid }: { className: string; text: string; isValid: boolean }) => {
        let styleClass = 'text-secondary';

        if (resetPasswordForm?.touched?.password && resetPasswordForm?.errors?.password && !isValid) {
            styleClass = 'text-danger';
        }

        if (isValid) {
            styleClass = 'text-success';
        }

        return (
            <div className={`${className} ${styleClass}`}>
                {!isValid ? <i className="ri-close-circle-fill"></i> : <i className="ri-checkbox-circle-fill"></i>}
                <small className="ms-2">{text}</small>
            </div>
        );
    };

    return (
        <>
            <MlxToast
                data-testid="toast"
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
            <div className="login-page-container row m-0 h-100 w-100">
                <div className="col-md-6 d-none d-md-block d-flex flex-column align-items-center justify-content-center login-page-img"></div>
                <div className="col-md-6 bg-white d-flex flex-column align-items-center justify-content-between shadow">
                    <div></div>
                    <div className="login-card">
                        <SoltBrandingLogo src={logo} alt="logo" isLoading={isLogoLoading} />
                        <div className="w-100 mt-4 justify-content-start">
                            <div className="fw-bold fs-6">{translatedText.SET_NEW_PASSWORD}</div>
                            <div className="text-muted small">{translatedText.SET_NEW_PASSWORD_MESSAGE}</div>
                        </div>
                        <form className="w-100 login-form d-flex flex-column align-items-center" onSubmit={resetPasswordForm.handleSubmit}>
                            <div className="w-100 mt-3 position-relative d-flex align-items-center">
                                <InputText className={`w-100 p-inputtext-sm`} value={userName} disabled />
                            </div>
                            <div className="w-100 mt-3 position-relative d-flex align-items-center">
                                <InputText
                                    className={`w-100 p-inputtext-sm ${resetPasswordForm?.touched?.password && resetPasswordForm?.errors?.password ? 'border-danger' : ''}`}
                                    type={showPassword ? 'text' : 'password'}
                                    value={resetPasswordForm?.values?.password}
                                    onChange={(event) => {
                                        resetPasswordForm.setFieldTouched('password', true);
                                        resetPasswordForm.setFieldValue('password', event.target.value, true);
                                    }}
                                    autoComplete="new-password"
                                    placeholder={translatedText.PASSWORD}
                                />
                                <Button
                                    className="position-absolute end-0"
                                    icon={showPassword ? 'ri-eye-fill' : 'ri-eye-off-fill'}
                                    rounded
                                    text
                                    size="large"
                                    severity="secondary"
                                    aria-label="Toggle password visibility"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setShowPassword(!showPassword);
                                    }}
                                    pt={{
                                        root: {
                                            style: {
                                                border: 'none',
                                                background: 'transparent',
                                                boxShadow: 'none',
                                            },
                                        },
                                    }}
                                />
                            </div>
                            <div className="w-100 mt-3 position-relative d-flex align-items-center">
                                <div className="row">
                                    <PasswordRequirement
                                        className="col-md-6 d-flex align-items-center"
                                        text={translatedText.PASSWORD_CHECK_LOWERCASE}
                                        isValid={passwordCriteria.lowercase}
                                    />
                                    <PasswordRequirement
                                        className="col-md-6 d-flex align-items-center"
                                        text={translatedText.PASSWORD_CHECK_UPPERCASE}
                                        isValid={passwordCriteria.uppercase}
                                    />
                                    <PasswordRequirement
                                        className="col-md-6 d-flex align-items-center"
                                        text={translatedText.PASSWORD_CHECK_NUMBER}
                                        isValid={passwordCriteria.number}
                                    />
                                    <PasswordRequirement
                                        className="col-md-6 d-flex align-items-center"
                                        text={translatedText.PASSWORD_CHECK_MIN}
                                        isValid={passwordCriteria.length}
                                    />
                                    <PasswordRequirement
                                        className="col-md-12 d-flex align-items-center"
                                        text={translatedText.PASSWORD_CHECK_SPECIAL}
                                        isValid={passwordCriteria.special}
                                    />
                                </div>
                            </div>

                            <div className="w-100 mt-3 position-relative d-flex align-items-center">
                                <InputText
                                    className={`w-100 p-inputtext-sm ${resetPasswordForm?.touched?.confirmPassword && resetPasswordForm?.errors?.confirmPassword ? 'border-danger' : ''}`}
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={resetPasswordForm?.values?.confirmPassword}
                                    onChange={(event) => {
                                        resetPasswordForm.setFieldTouched('confirmPassword', true);
                                        resetPasswordForm.setFieldValue('confirmPassword', event.target.value, true);
                                    }}
                                    autoComplete="new-password"
                                    placeholder={translatedText.CONFIRM_PASSWORD}
                                />
                                <Button
                                    className="position-absolute end-0"
                                    icon={showConfirmPassword ? 'ri-eye-fill' : 'ri-eye-off-fill'}
                                    rounded
                                    text
                                    size="large"
                                    severity="secondary"
                                    aria-label="Toggle password visibility"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setShowConfirmPassword(!showConfirmPassword);
                                    }}
                                    pt={{
                                        root: {
                                            style: {
                                                border: 'none',
                                                background: 'transparent',
                                                boxShadow: 'none',
                                            },
                                        },
                                    }}
                                />
                            </div>
                            {resetPasswordForm.touched.confirmPassword && resetPasswordForm.errors.confirmPassword && (
                                <div className="text-danger small">{resetPasswordForm.errors.confirmPassword}</div>
                            )}

                            <div className="w-100 d-flex align-items-center">
                                <Messages className="w-100" ref={messageRef} pt={{ wrapper: { style: { padding: '0.5em 1rem' } } }} />
                            </div>

                            <MlxButton
                                type="submit"
                                label={translatedText.SET_NEW_PASSWORD}
                                className="mt-3 w-100"
                                color="primary"
                                raised
                                loading={resetPasswordMutation.isLoading || isLoading}
                            />
                        </form>
                    </div>

                    <small className="mb-2 d-flex flex-row justify-content-center align-items-center">
                        <i className="ri-copyright-line"></i>
                        <span className="ms-1">
                            {APP_YEAR} {APP_NAME}, {translatedText.FOOTER_BRAND}
                        </span>
                    </small>
                </div>
            </div>
        </>
    );
};

export default SetNewPassword;
